import { createRef } from "react";

import clsx from "clsx";

import { AjaxMethod } from "@Components/Modules/Ajax";

import type { IComponentAjax, IComponentAjaxState } from "../ComponentAjax";
import { ComponentAjax } from "../ComponentAjax";
import type { IInput } from "../Input";
import { Loading } from "../Loading";
import { htmlEncode } from "../Modules/Helpers";
import type { TextboxType } from "../Textbox";
import { Textbox, TextboxAppearance } from "../Textbox";

import * as css from "./SearchBox.module.css";

export interface ISearchBox extends IComponentAjax<ISearchBoxResult>, IInput {
    textboxType?: TextboxType;
    passwordToggle?: boolean;
    lockToggle?: boolean;
    appearance?: TextboxAppearance;
    value?: string;
    watermarkText?: string;
    title?: string;
    passwordToggleTitle?: string;
    maxLength?: number;
    searchListMaxHeight?: number;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => boolean;
    onKeyDown?: (event: React.KeyboardEvent<HTMLElement>, item: ISearchBoxResultItem) => boolean | void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    isReadOnly?: boolean;
    disableAutocomplete?: boolean;
    searchMinimumLength: number;
    searchParameter: string;
    onSelect: (event: React.SyntheticEvent<HTMLElement>, item: ISearchBoxResultItem) => boolean;
    searchDelay: number;
    onBlur?: () => void;
    onBlurSelectedItem?: (item: ISearchBoxResultItem) => void;
    hideResults: boolean;
    preloadData: boolean;
}

export interface SearchBoxState extends IComponentAjaxState {
    searchResult: ISearchBoxResultItem[];
    searchError: boolean;
    isLoading: boolean;
    value?: string;
    searchData?: ISearchBoxResultItem[];
    dropdownIsOpen: boolean;
}

export interface ISearchBoxResult {
    matches: ISearchBoxResultItem[];
}

export interface ISearchBoxResultItem {
    text: string;
    id?: string;
    targetUrl?: string;
}

export class SearchBox extends ComponentAjax<ISearchBoxResult, ISearchBox, SearchBoxState> {
    public static defaultProps: ISearchBox = {
        ...ComponentAjax.defaultProps,
        ...Textbox.defaultProps,
        watermarkText: null,
        title: null,
        appearance: TextboxAppearance.Search,
        searchMinimumLength: 3,
        searchParameter: null,
        onChange: null,
        onSelect: null,
        onKeyDown: null,
        searchDelay: 0,
        value: "",
        onFocus: null,
        onBlur: null,
        hideResults: false,
        preloadData: false,
    };

    private textboxRef = createRef<HTMLInputElement>();

    private searchItemsIndex: number = -1;

    private searchCall: number = null;

    private searchItems: { [id: string]: HTMLAnchorElement } = null;

    private initialAjaxRequest: boolean = true;

    constructor(props: ISearchBox) {
        super({ ...props, ajaxMethod: props.ajaxMethod == undefined ? AjaxMethod.Post : props.ajaxMethod });
        this.state = {
            searchResult:
                this.props.initialData && this.props.initialData.matches
                    ? this.props.initialData.matches
                    : [],
            searchError: false,
            isLoading: false,
            value: this.props.value,
            searchData:
                this.props.initialData && this.props.initialData.matches
                    ? this.props.initialData.matches
                    : undefined,
            dropdownIsOpen: false,
        };
    }

    public componentDidMount() {
        if (this.props.preloadData === true && this.state.searchResult.length === 0) {
            this.ajaxRequest();
        } else {
            this.initialAjaxRequest = false;
        }
    }

    public UNSAFE_componentWillReceiveProps(nextProps: ISearchBox) {
        if (nextProps.initialData && nextProps.initialData !== this.props.initialData) {
            this.setState({
                searchResult: nextProps.initialData.matches ? nextProps.initialData.matches : [],
                dropdownIsOpen: !!(nextProps.value && nextProps.value !== ""),
            });
        }

        if (nextProps.value || nextProps.value === "") {
            this.setState({ value: nextProps.value });
        }
    }

    public componentDidUpdate(_prevProps: ISearchBox, prevState: SearchBoxState) {
        if (prevState.dropdownIsOpen === true && prevState.dropdownIsOpen !== this.state.dropdownIsOpen) {
            document.body.removeEventListener("click", this.handleClick, false);
        } else if (
            prevState.dropdownIsOpen === false &&
            prevState.dropdownIsOpen !== this.state.dropdownIsOpen
        ) {
            document.body.addEventListener("click", this.handleClick, false);
        }
    }

    public componentWillUnmount() {
        document.body.removeEventListener("click", this.handleClick, false);

        super.componentWillUnmount();
    }

    public render() {
        return (
            <div {...this.rootDefaultAttributes(css.searchBoxComponent)}>
                <Textbox
                    {...this.props}
                    clientId={this.props.clientId}
                    forwardRef={this.textboxRef}
                    onChange={this.onChange}
                    onKeyDown={this.onKeyDown}
                    onFocus={this.onTextboxFocus}
                    onBlur={this.onBlur}
                    value={this.state.value}
                    maxLength={20}
                    disableAutoComplete={true}
                />
                {this.renderAjaxErrorContainer()}
                {this.renderResults()}
            </div>
        );
    }

    protected ajaxSuccess(response: ISearchBoxResult): void {
        if (this.initialAjaxRequest) {
            this.initialAjaxRequest = false;
            this.setState({ searchData: response.matches });
        } else {
            this.setState({
                searchResult: response ? response.matches : [],
                searchError: false,
                isLoading: false,
                dropdownIsOpen: true,
            });
        }
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false });
        return true;
    }

    private onKeyDown = (
        event: React.KeyboardEvent<HTMLElement>,
        item: ISearchBoxResultItem | null = null,
    ): boolean => {
        if (this.props.onKeyDown && this.props.onKeyDown(event, item) === false) {
            return false;
        }

        if (this.props.hideResults === true) {
            return true;
        }

        if (event.key === "Enter") {
            event.stopPropagation();
            event.preventDefault();

            const params: { [key: string]: string } = {};

            if (this.props.searchParameter && this.state.value) {
                params[this.props.searchParameter] = this.state.value;
            }

            const resultItem = item || {
                text: this.state.value,
                targetUrl: null,
                id: null,
            };

            const result = this.onSelect(event, resultItem);

            if (result === false) {
                return result;
            }

            if (this.state.dropdownIsOpen === true) {
                this.setState({ dropdownIsOpen: false });
            }
        }

        if (this.state.dropdownIsOpen === false) {
            return true;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowDown") {
            event.stopPropagation();
            event.preventDefault();

            if (event.key === "ArrowDown" && this.searchItems[`SearchItem${this.searchItemsIndex + 1}`]) {
                this.searchItemsIndex += 1;
            } else if (
                event.key === "ArrowDown" &&
                this.searchItems[`SearchItem${this.searchItemsIndex - 1}`]
            ) {
                this.searchItemsIndex -= 1;
            }

            this.searchItems[`SearchItem${this.searchItemsIndex}`].focus();
            return false;
        }

        if (event.key === "Escape" && this.state.searchResult && this.state.searchResult.length > 0) {
            event.stopPropagation();
            event.preventDefault();
            this.setState({ searchResult: [] });
        }

        return true;
    };

    private onItemFocus = (event: React.FocusEvent<HTMLAnchorElement>): void =>
        this.setState({ value: event.currentTarget.innerText });

    private onTextboxFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
        this.searchItemsIndex = -1;

        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    };

    private filterResults = (value: string) => {
        if (!this.state.searchData) {
            return;
        }

        let results: ISearchBoxResultItem[] = [];

        for (let i = 0; i < this.state.searchData.length; i += 1) {
            const item = this.state.searchData[i];
            const startIndex = item.text.toLowerCase().indexOf(value.toLowerCase());

            if (value.length === 0 || startIndex !== -1) {
                results = [...results, { ...item, targetUrl: undefined }];
            }
        }

        this.setState({ value, searchResult: results, dropdownIsOpen: true });
    };

    private onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        let result = true;

        if (this.props.onChange) {
            result = this.props.onChange(event);
        }

        if (result === false) {
            return;
        }

        if (this.props.preloadData === true) {
            this.filterResults(value);
            return;
        }

        if (value.length < this.props.searchMinimumLength) {
            this.setState({
                value,
                searchResult: [],
                searchError: false,
                isLoading: false,
            });

            this.abortAjaxRequest();

            if (this.searchCall) {
                clearTimeout(this.searchCall);
            }

            return;
        }

        const newState = { ...this.state, value };

        if (this.props.ajaxAction) {
            newState.isLoading = true;
        }

        if (this.searchCall) {
            clearTimeout(this.searchCall);
        }

        this.setState({ value });

        if (this.props.searchDelay === 0) {
            this.search(value);
            return;
        }

        this.setState(newState);
        this.searchCall = window.setTimeout(() => {
            this.search(value);
        }, this.props.searchDelay);
    };

    private search = (value: string) => {
        this.searchItemsIndex = -1;

        if (!this.props.ajaxAction) {
            return;
        }

        const param: { [key: string]: string } = {};
        if (this.props.searchParameter) {
            param[this.props.searchParameter] = htmlEncode(value);
        }

        this.ajaxRequest(param);
    };

    private handleClick = (e: MouseEvent) => {
        if (e.target !== this.textboxRef.current) {
            if (this.state.dropdownIsOpen) {
                this.setState({ dropdownIsOpen: false });
            }
        }
    };

    private onSelect = (e: React.SyntheticEvent<HTMLElement>, item: ISearchBoxResultItem): boolean => {
        let response = true;

        if (this.props.onSelect) {
            response = this.props.onSelect(e, item);
        }

        if (response !== false && item) {
            this.setState({ searchResult: [] });
        }

        return response;
    };

    private onBlur = () => {
        if (this.props.onBlurSelectedItem) {
            if (this.state.value && this.state.searchResult && this.state.searchResult.length === 1) {
                const resultItem = this.state.searchResult[0];
                this.props.onBlurSelectedItem(resultItem);
            } else {
                this.props.onBlurSelectedItem(null);
            }
        }

        if (this.props.onBlur) {
            this.props.onBlur();
        }
    };

    private renderSearchItems = () => {
        if (!this.state.searchResult || this.state.searchResult.length === 0) {
            if (this.state.searchError === true) {
                return this.renderAjaxErrorContainer();
            }

            return null;
        }

        if (this.state.dropdownIsOpen === false) {
            return null;
        }

        this.searchItems = {};

        return (
            <ul className={css.searchList}>
                {this.state.searchResult.map((item, i) => (
                    <li className={css.searchItem} key={i}>
                        <a
                            href={item.targetUrl}
                            ref={(ref) => (this.searchItems[`SearchItem${i}`] = ref)}
                            onMouseDown={(event: React.MouseEvent<HTMLAnchorElement>) =>
                                this.onSelect(event, item)
                            }
                            onKeyDown={(event: React.KeyboardEvent<HTMLElement>) =>
                                this.onKeyDown(event, item)
                            }
                            onFocus={this.onItemFocus}
                            dangerouslySetInnerHTML={{ __html: item.text }}
                        />
                    </li>
                ))}
            </ul>
        );
    };

    private renderResults = () => {
        if (this.props.hideResults === true) {
            return null;
        }

        if (
            (!this.state.searchResult || this.state.searchResult.length === 0) &&
            this.state.isLoading === false
        ) {
            return null;
        }

        return (
            <div
                className={clsx(css.searchResults, this.state.isLoading && css.isLoading)}
                style={{ maxHeight: this.props.searchListMaxHeight }}
            >
                {this.state.isLoading && <Loading isModal />}
                {this.renderSearchItems()}
            </div>
        );
    };
}
